'use client';

import { useState } from 'react';
import {
  Package,
  Truck,
  CreditCard,
  ShoppingCart,
  Heart,
  Flower2,
  MessageCircle,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import Link from 'next/link';

type FAQItem = {
  question: string;
  answer: string;
};

type FAQCategory = {
  icon: React.ReactNode;
  title: string;
  items: FAQItem[];
};

export default function HelpPageClient() {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);

  const toggleAccordion = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setExpandedIndex(expandedIndex === key ? null : key);
  };

  const faqCategories: FAQCategory[] = [
    {
      icon: <ShoppingCart className="h-5 w-5" strokeWidth={2.75} />,
      title: t('help.ordering.title'),
      items: [
        {
          question: t('help.ordering.howToOrder'),
          answer: t('help.ordering.howToOrderAnswer')
        },
        {
          question: t('help.ordering.modifyOrder'),
          answer: t('help.ordering.modifyOrderAnswer')
        },
        {
          question: t('help.ordering.cancelOrder'),
          answer: t('help.ordering.cancelOrderAnswer')
        },
        {
          question: t('help.ordering.trackOrder'),
          answer: t('help.ordering.trackOrderAnswer')
        }
      ]
    },
    {
      icon: <Truck className="h-5 w-5" strokeWidth={2.75} />,
      title: t('help.delivery.title'),
      items: [
        {
          question: t('help.delivery.deliveryTime'),
          answer: t('help.delivery.deliveryTimeAnswer')
        },
        {
          question: t('help.delivery.sameDayDelivery'),
          answer: t('help.delivery.sameDayDeliveryAnswer')
        },
        {
          question: t('help.delivery.deliveryAreas'),
          answer: t('help.delivery.deliveryAreasAnswer')
        },
        {
          question: t('help.delivery.notHome'),
          answer: t('help.delivery.notHomeAnswer')
        }
      ]
    },
    {
      icon: <CreditCard className="h-5 w-5" strokeWidth={2.75} />,
      title: t('help.payment.title'),
      items: [
        {
          question: t('help.payment.paymentMethods'),
          answer: t('help.payment.paymentMethodsAnswer')
        },
        {
          question: t('help.payment.securePayment'),
          answer: t('help.payment.securePaymentAnswer')
        },
        {
          question: t('help.payment.cashOnDelivery'),
          answer: t('help.payment.cashOnDeliveryAnswer')
        }
      ]
    },
    {
      icon: <Flower2 className="h-5 w-5" strokeWidth={2.75} />,
      title: t('help.flowers.title'),
      items: [
        {
          question: t('help.flowers.flowerCare'),
          answer: t('help.flowers.flowerCareAnswer')
        },
        {
          question: t('help.flowers.howLong'),
          answer: t('help.flowers.howLongAnswer')
        },
        {
          question: t('help.flowers.customBouquet'),
          answer: t('help.flowers.customBouquetAnswer')
        },
        {
          question: t('help.flowers.freshness'),
          answer: t('help.flowers.freshnessAnswer')
        }
      ]
    },
    {
      icon: <Package className="h-5 w-5" strokeWidth={2.75} />,
      title: t('help.products.title'),
      items: [
        {
          question: t('help.products.outOfStock'),
          answer: t('help.products.outOfStockAnswer')
        },
        {
          question: t('help.products.addOns'),
          answer: t('help.products.addOnsAnswer')
        }
      ]
    },
    {
      icon: <Heart className="h-5 w-5" strokeWidth={2.75} />,
      title: t('help.account.title'),
      items: [
        {
          question: t('help.account.wishlist'),
          answer: t('help.account.wishlistAnswer')
        },
        {
          question: t('help.account.orderHistory'),
          answer: t('help.account.orderHistoryAnswer')
        }
      ]
    }
  ];

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    items: category.items.filter(item => 
      searchQuery === '' ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.items.length > 0);

  return (
    <main className="organic min-h-screen">
      {/* Hero */}
      <section
        className="relative overflow-hidden text-center"
        style={{ padding: 'calc(64px + var(--space-8) * 1.4) clamp(20px, 4vw, 48px) var(--space-6)' }}
      >
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ left: '50%', top: -140, transform: 'translateX(-50%)', width: 320, height: 320, background: 'var(--color-accent-2-200)', zIndex: 0 }}
        />
        <div className="mx-auto relative" style={{ zIndex: 1, maxWidth: 620 }}>
          <h1 style={{ fontSize: 'clamp(30px, 4vw, 44px)', lineHeight: 1.08, margin: '0 0 var(--space-3)' }}>
            {t('help.title')}
          </h1>
          <p
            style={{
              fontSize: 16.5, lineHeight: 1.6,
              color: 'color-mix(in srgb, var(--color-text) 78%, transparent)',
              margin: '0 0 var(--space-4)',
            }}
          >
            {t('help.subtitle')}
          </p>
          <input
            type="text"
            className="input mx-auto"
            style={{ maxWidth: 480 }}
            placeholder={t('help.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </section>

      <div className="mx-auto" style={{ maxWidth: 900, padding: '0 clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)' }}>
        {/* Quick links */}
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
          <Link href="/orders" className="card elev-sm flex items-center" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)', textDecoration: 'none' }}>
            <Package className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-text)' }}>{t('help.quickLinks.trackOrder')}</p>
          </Link>
          <Link href="/contact" className="card elev-sm flex items-center" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)', textDecoration: 'none' }}>
            <MessageCircle className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-text)' }}>{t('help.quickLinks.contactUs')}</p>
          </Link>
          <Link href="/shop" className="card elev-sm flex items-center" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)', textDecoration: 'none' }}>
            <Flower2 className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-text)' }}>{t('help.quickLinks.browseFlowers')}</p>
          </Link>
        </div>

        {filteredCategories.length === 0 && searchQuery && (
          <div className="text-center" style={{ padding: 'var(--space-8) 0' }}>
            <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', fontSize: 17 }}>{t('help.noResults')}</p>
          </div>
        )}

        {/* FAQ categories */}
        {filteredCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="card elev-sm" style={{ marginBottom: 'var(--space-4)', padding: 0, overflow: 'hidden' }}>
            <div
              className="flex items-center"
              style={{ padding: 'var(--space-3) var(--space-6)', background: 'var(--color-accent-100)', gap: 'var(--space-2)', borderBottom: '1px solid var(--color-divider)' }}
            >
              <span style={{ color: 'var(--color-accent-700)', display: 'flex' }}>{category.icon}</span>
              <h3 style={{ margin: 0, fontSize: 17 }}>{category.title}</h3>
            </div>

            {category.items.map((item, itemIndex) => {
              const key = `${categoryIndex}-${itemIndex}`;
              const isExpanded = expandedIndex === key;

              return (
                <div key={itemIndex} style={{ borderBottom: '1px solid var(--color-divider)' }}>
                  <button
                    onClick={() => toggleAccordion(categoryIndex, itemIndex)}
                    className="flex items-center justify-between"
                    style={{
                      width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                      padding: 'var(--space-3) var(--space-6)', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-text)',
                    }}
                  >
                    <span>{item.question}</span>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 flex-shrink-0" strokeWidth={2.75} />
                    ) : (
                      <ChevronDown className="h-4 w-4 flex-shrink-0" strokeWidth={2.75} />
                    )}
                  </button>

                  {isExpanded && (
                    <p
                      className="whitespace-pre-line"
                      style={{
                        margin: 0, padding: '0 var(--space-6) var(--space-4)',
                        color: 'color-mix(in srgb, var(--color-text) 70%, transparent)', fontSize: 14, lineHeight: 1.6,
                      }}
                    >
                      {item.answer}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Closing contact band */}
      <section style={{ background: 'var(--color-neutral-100)', padding: 'calc(var(--space-8) * 1.2) clamp(20px, 4vw, 48px)', textAlign: 'center' }}>
        <h2 style={{ margin: '0 0 var(--space-2)', fontSize: 26 }}>{t('help.stillNeedHelp.title')}</h2>
        <p style={{ color: 'color-mix(in srgb, var(--color-text) 70%, transparent)', margin: '0 0 var(--space-6)' }}>
          {t('help.stillNeedHelp.subtitle')}
        </p>

        <div
          className="mx-auto grid grid-cols-1 md:grid-cols-3"
          style={{ maxWidth: 800, gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}
        >
          <div className="card elev-sm text-center" style={{ padding: 'var(--space-6)' }}>
            <Phone className="h-5 w-5 mx-auto" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)', marginBottom: 'var(--space-2)' }} />
            <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{t('help.contact.phone')}</p>
            <p style={{ margin: '0 0 2px', fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>0769 141 250</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'color-mix(in srgb, var(--color-text) 55%, transparent)' }}>{t('contact.phoneSchedule')}</p>
          </div>

          <div className="card elev-sm text-center" style={{ padding: 'var(--space-6)' }}>
            <Mail className="h-5 w-5 mx-auto" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)', marginBottom: 'var(--space-2)' }} />
            <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{t('help.contact.email')}</p>
            <a href="mailto:laurasimona97@yahoo.com" style={{ display: 'block', fontSize: 14 }}>laurasimona97@yahoo.com</a>
            <a href="mailto:simonabuzau2@gmail.com" style={{ display: 'block', fontSize: 14 }}>simonabuzau2@gmail.com</a>
          </div>

          <div className="card elev-sm text-center" style={{ padding: 'var(--space-6)' }}>
            <MessageCircle className="h-5 w-5 mx-auto" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)', marginBottom: 'var(--space-2)' }} />
            <p style={{ margin: '0 0 var(--space-2)', fontWeight: 600 }}>{t('help.contact.message')}</p>
            <Link href="/contact" className="btn btn-primary">{t('help.contact.sendMessage')}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
